import { useEffect } from 'react';
import {
  COOKIE_CONSENT_EVENT,
  hasAnalyticsConsent,
  type CookieConsentState,
} from '../../utils/cookieConsent';

type TawkApi = {
  showWidget?: () => void;
  hideWidget?: () => void;
  maximize?: () => void;
  toggle?: () => void;
  onLoad?: () => void;
};

export function openTawkChat() {
  if (!hasAnalyticsConsent()) return;
  const api = (window as Window & { Tawk_API?: TawkApi }).Tawk_API;
  if (!api) return;
  api.showWidget?.();
  if (typeof api.maximize === 'function') {
    api.maximize();
  } else {
    api.toggle?.();
  }
}

/** Mount Tawk.to live chat on marketing pages; hide on unmount. Gated by cookie consent. */
export function useTawkTo() {
  useEffect(() => {
    const win = window as Window & { Tawk_API?: TawkApi; Tawk_LoadStart?: Date };
    let cancelled = false;
    let cleanupWidget: (() => void) | null = null;

    const mountWidget = () => {
      if (cancelled || cleanupWidget) return;

      win.Tawk_API = win.Tawk_API || {};
      win.Tawk_LoadStart = new Date();

      const HIDE_CSS = `
      img[alt="Chat attention grabber"],
      img[src*="attention-grabbers"] {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
        opacity: 0 !important;
        width: 0 !important;
        height: 0 !important;
      }
    `;
      const STYLE_ID = 'tawkto-hide-attention-grabber';
      const GRABBER_SEL =
        'img[alt="Chat attention grabber"], img[src*="attention-grabbers"]';

      const iframeObservers = new Map<Document, MutationObserver>();
      const observedIframes = new WeakSet<HTMLIFrameElement>();

      const ensureStyle = (doc: Document) => {
        try {
          if (doc.getElementById(STYLE_ID)) return;
          const style = doc.createElement('style');
          style.id = STYLE_ID;
          style.textContent = HIDE_CSS;
          (doc.head || doc.documentElement).appendChild(style);
        } catch {
          /* ignore */
        }
      };

      const scrubDoc = (doc: Document | null | undefined) => {
        if (!doc) return;
        try {
          ensureStyle(doc);
          doc.querySelectorAll<HTMLImageElement>(GRABBER_SEL).forEach((img) => {
            const wrap = img.parentElement;
            img.style.setProperty('display', 'none', 'important');
            img.remove();
            if (wrap && wrap !== doc.body) {
              const stillUseful = wrap.querySelector(
                'button, a, input, textarea, iframe, [role="button"]'
              );
              if (!stillUseful) {
                wrap.style.setProperty('display', 'none', 'important');
                wrap.style.setProperty('visibility', 'hidden', 'important');
              }
            }
          });
        } catch {
          /* ignore */
        }
      };

      const watchIframeDoc = (iframe: HTMLIFrameElement, doc: Document) => {
        if (iframeObservers.has(doc)) return;
        scrubDoc(doc);
        const obs = new MutationObserver(() => scrubDoc(doc));
        obs.observe(doc.documentElement || doc, {
          childList: true,
          subtree: true,
          attributes: true,
        });
        iframeObservers.set(doc, obs);
      };

      const handleIframe = (iframe: HTMLIFrameElement) => {
        try {
          const doc = iframe.contentDocument;
          if (!doc) return;
          const hasGrabber = !!doc.querySelector(GRABBER_SEL);
          const isBubble =
            /opens the chat|opens chat/i.test(doc.body?.innerText || '') ||
            iframe.offsetWidth <= 80;
          if (hasGrabber && !isBubble) {
            iframe.style.setProperty('display', 'none', 'important');
            iframe.style.setProperty('visibility', 'hidden', 'important');
            iframe.style.setProperty('pointer-events', 'none', 'important');
            iframe.style.setProperty('width', '0', 'important');
            iframe.style.setProperty('height', '0', 'important');
          }
          scrubDoc(doc);
          watchIframeDoc(iframe, doc);
          if (!observedIframes.has(iframe)) {
            observedIframes.add(iframe);
            iframe.addEventListener('load', () => {
              if (!cancelled) handleIframe(iframe);
            });
          }
        } catch {
          /* cross-origin */
        }
      };

      const scan = () => {
        if (cancelled) return;
        scrubDoc(document);
        document.querySelectorAll('iframe').forEach((iframe) => {
          handleIframe(iframe as HTMLIFrameElement);
        });
      };

      ensureStyle(document);
      scan();

      const rootObserver = new MutationObserver(() => scan());
      rootObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });

      const pollId = window.setInterval(scan, 500);
      const stopPollId = window.setTimeout(() => clearInterval(pollId), 20000);

      const prevOnLoad = win.Tawk_API.onLoad;
      win.Tawk_API.onLoad = () => {
        prevOnLoad?.();
        scan();
        [100, 500, 1000, 2000, 4000].forEach((ms) => window.setTimeout(scan, ms));
      };

      const SCRIPT_ID = 'tawkto-script';
      const existing = document.getElementById(SCRIPT_ID);
      if (existing) {
        win.Tawk_API.showWidget?.();
        scan();
      } else {
        const s1 = document.createElement('script');
        s1.id = SCRIPT_ID;
        s1.async = true;
        s1.src = 'https://embed.tawk.to/6a660157bc55a11d4d70fd38/1juf7afgu';
        s1.charset = 'UTF-8';
        s1.setAttribute('crossorigin', '*');
        const s0 = document.getElementsByTagName('script')[0];
        s0.parentNode?.insertBefore(s1, s0);
      }

      cleanupWidget = () => {
        rootObserver.disconnect();
        clearInterval(pollId);
        clearTimeout(stopPollId);
        iframeObservers.forEach((obs) => obs.disconnect());
        iframeObservers.clear();
        win.Tawk_API?.hideWidget?.();
      };
    };

    const hideWidget = () => {
      cleanupWidget?.();
      cleanupWidget = null;
      win.Tawk_API?.hideWidget?.();
    };

    const sync = () => {
      if (hasAnalyticsConsent()) mountWidget();
      else hideWidget();
    };

    const onConsent = (event: Event) => {
      const detail = (event as CustomEvent<CookieConsentState>).detail;
      if (detail?.analytics) mountWidget();
      else hideWidget();
    };

    sync();
    window.addEventListener(COOKIE_CONSENT_EVENT, onConsent);

    return () => {
      cancelled = true;
      window.removeEventListener(COOKIE_CONSENT_EVENT, onConsent);
      hideWidget();
    };
  }, []);
}
